import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button } from '../../components/button'
import { Status } from '../../components/cards/statusTag'
import { ConfirmationModal } from '../../components/modal/app_modals/confirmationModal'
import { TableComponent } from '../../components/table'
import { useModalControl } from '../../hooks/useModalControl'
import {
  getArchiveInterestConfirmTitle,
  INTEREST_ARCHIVE_CONFIRM,
  INTEREST_MESSAGE,
  INTEREST_ROUTE,
  INTEREST_STATUS,
  INTEREST_STATUS_LABEL,
} from '../../redux-toolkits/interests/interests.constant'
import {
  buildArchiveInterestPayload,
  buildRestoreInterestPayload,
  compareInterestsByDisplayOrder,
  filterInterestsByStatus,
  getInterestEditRoute,
  getInterestErrorMessage,
} from '../../redux-toolkits/interests/interests.helpers'
import {
  useGetAllInterestsQuery,
  useUpdateInterestMutation,
} from '../../redux-toolkits/interests/interests.slice'
import type {
  InterestFilter,
  InterestStatus,
  ShoppingInterest,
} from '../../redux-toolkits/interests/interests.type'
import type { StatusTone } from '../../redux-toolkits/shared/shared.type'
const FILTER_OPTIONS: Array<{ value: InterestFilter; label: string }> = [
  { value: 'all', label: 'All' },
  {
    value: INTEREST_STATUS.ACTIVE,
    label: INTEREST_STATUS_LABEL[INTEREST_STATUS.ACTIVE],
  },
  {
    value: INTEREST_STATUS.DRAFT,
    label: INTEREST_STATUS_LABEL[INTEREST_STATUS.DRAFT],
  },
  {
    value: INTEREST_STATUS.ARCHIVED,
    label: INTEREST_STATUS_LABEL[INTEREST_STATUS.ARCHIVED],
  },
]

const STATUS_TYPE: Record<InterestStatus, StatusTone> = {
  [INTEREST_STATUS.ACTIVE]: 'success',
  [INTEREST_STATUS.DRAFT]: 'warn',
  [INTEREST_STATUS.ARCHIVED]: 'fail',
}

/**
 * Displays the complete Shopping Interest catalogue and owns lifecycle
 * actions. Writes intentionally omit the legacy active and Product Tag fields.
 * Rendered as the Interests tab of the Shopping Preferences page.
 */
export const InterestsPanel = () => {
  const navigate = useNavigate()
  const [selectedFilter, setSelectedFilter] = useState<InterestFilter>('all')
  const [selectedInterest, setSelectedInterest] = useState<
    ShoppingInterest | undefined
  >(undefined)
  const { modal, modalOpen, setModalOpen, handleModalOpen, handleModalClose } =
    useModalControl()
  const {
    data: interests = [],
    isLoading,
    isError,
    refetch,
  } = useGetAllInterestsQuery(null, {
    refetchOnMountOrArgChange: true,
  })
  const [updateInterest, { isLoading: isUpdating }] =
    useUpdateInterestMutation()

  const visibleInterests = useMemo(
    () =>
      filterInterestsByStatus(interests, selectedFilter).sort(
        compareInterestsByDisplayOrder,
      ),
    [interests, selectedFilter],
  )

  /**
   * Clears archive-modal selection and closes the confirmation dialog.
   */
  const closeArchiveConfirmModal = (): void => {
    setSelectedInterest(undefined)
    handleModalClose()
  }

  /**
   * Archives a Shopping Interest after the custom confirmation modal has
   * already been accepted. The backend retains mappings and history, so this
   * never behaves like a hard delete.
   */
  const archiveInterest = async (interest: ShoppingInterest): Promise<void> => {
    try {
      await updateInterest({
        interestId: interest._id,
        body: buildArchiveInterestPayload(),
      }).unwrap()
      toast.success(INTEREST_MESSAGE.ARCHIVED)
      closeArchiveConfirmModal()
    } catch (error: unknown) {
      toast.error(getInterestErrorMessage(error))
    }
  }

  /**
   * Restores an Archived interest as Draft at the end of the current
   * non-archived order, requiring a separate decision before publication.
   */
  const restoreInterest = async (interest: ShoppingInterest): Promise<void> => {
    try {
      await updateInterest({
        interestId: interest._id,
        body: buildRestoreInterestPayload(interests),
      }).unwrap()
      toast.success(INTEREST_MESSAGE.RESTORED)
    } catch (error: unknown) {
      toast.error(getInterestErrorMessage(error))
    }
  }

  const rows = visibleInterests.map((interest) => ({
    id: interest._id,
    content: [
      interest.status === INTEREST_STATUS.ARCHIVED
        ? '—'
        : interest.displayOrder,
      <div key={`${interest._id}-details`} className="max-w-[360px]">
        <p className="font-medium text-black">{interest.name}</p>
        <p className="mt-1 whitespace-normal text-sm text-pry-gray">
          {interest.description !== ''
            ? interest.description
            : 'No description'}
        </p>
      </div>,
      <Status
        key={`${interest._id}-status`}
        type={STATUS_TYPE[interest.status]}
        text={INTEREST_STATUS_LABEL[interest.status]}
      />,
      <span
        key={`${interest._id}-categories`}
        className="block max-w-[320px] whitespace-normal"
      >
        {interest.categories.length > 0
          ? interest.categories.map(({ name }) => name).join(', ')
          : 'No categories'}
      </span>,
      interest.status === INTEREST_STATUS.ARCHIVED ? (
        <button
          key={`${interest._id}-restore`}
          type="button"
          disabled={isUpdating}
          className="font-medium text-primary disabled:opacity-50"
          onClick={() => {
            void restoreInterest(interest)
          }}
        >
          Restore
        </button>
      ) : (
        <div
          key={`${interest._id}-actions`}
          className="flex items-center gap-4"
        >
          <button
            type="button"
            className="font-medium text-primary"
            onClick={() => {
              navigate(getInterestEditRoute(interest._id))
            }}
          >
            Edit
          </button>
          <button
            type="button"
            disabled={isUpdating}
            className="font-medium text-danger disabled:opacity-50"
            onClick={() => {
              setSelectedInterest(interest)
              handleModalOpen(INTEREST_ARCHIVE_CONFIRM.MODAL)
            }}
          >
            Archive
          </button>
        </div>
      ),
    ],
  }))

  return (
    <>
      <section className="space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="text-pry-gray">
            Manage the interests customers can choose during onboarding.
          </p>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Button
              label="Manage Order"
              size="medium"
              onClick={() => {
                navigate(INTEREST_ROUTE.ORDER)
              }}
            />
            <Button
              label="Create Interest"
              size="medium"
              primary
              onClick={() => {
                navigate(INTEREST_ROUTE.CREATE)
              }}
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2" aria-label="Filter by status">
          {FILTER_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              aria-pressed={selectedFilter === value}
              className={`rounded-full border px-5 py-2 text-sm transition-colors ${
                selectedFilter === value
                  ? 'border-black bg-black text-white'
                  : 'border-divider-gray bg-white text-black'
              }`}
              onClick={() => {
                setSelectedFilter(value)
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {isError ? (
          <div className="rounded-2xl border border-divider-gray p-8 text-center">
            <p className="text-danger">{INTEREST_MESSAGE.LOAD_ERROR}</p>
            <Button
              label="Try again"
              size="small"
              className="!mx-auto !mt-4 !w-auto"
              onClick={() => {
                void refetch()
              }}
            />
          </div>
        ) : (
          <TableComponent
            headers={['Order', 'Interest', 'Status', 'Categories', 'Actions']}
            rows={rows}
            name="shopping-interests"
            loading={isLoading}
            isEmpty={!isLoading && rows.length === 0}
            emptyText="No shopping interests match this filter."
          />
        )}
      </section>

      <ConfirmationModal
        open={modalOpen && modal === INTEREST_ARCHIVE_CONFIRM.MODAL}
        setOpen={setModalOpen}
        title={getArchiveInterestConfirmTitle(selectedInterest?.name ?? '')}
        message={INTEREST_ARCHIVE_CONFIRM.MESSAGE}
        confirmText={INTEREST_ARCHIVE_CONFIRM.CONFIRM_TEXT}
        loading={isUpdating}
        onCloseCallback={closeArchiveConfirmModal}
        onConfirm={() => {
          if (selectedInterest === undefined) return
          void archiveInterest(selectedInterest)
        }}
      />
    </>
  )
}
